import type { Theme, ThemeColor } from '@precis-digital/kurama/theme'

import { ASSETS_BASE_URL } from 'shared/constants'

import { PLATFORMS } from 'src/dataSource/constants'
import { PLATFORM } from 'src/dataSource/types'

import type { AttributionAxisProps, AttributionDimension, AttributionMetric } from './types'
import { CustomDataSourceType } from 'shared/api/attributionModels'
import { DIMENSION_OPERATORS_WITH_TYPES } from 'shared/constants/filterOperators'
import type { AlertType, LinePattern } from 'shared/components/ChartCard/types'
import { chartDataRowSort } from './utils'

export const CHANNEL = 'channel'
export const ADSPEND = 'adSpend'
export const DETAILS = 'details'
export const REVENUE = 'revenue'
export const ROAS = 'roas'
export const CONVERSIONS = 'conversions'
export const SESSIONS = 'sessions'
export const IMPRESSIONS = 'impressions'
export const COSTPERACTION = 'costPerAction'

export const DIFFERENCE = 'difference'
export const FIRSTSUBHEADER = 'firstHeader'
export const SECONDSUBHEADER = 'secondHeader'

export const attributionTableID = (modelId: number): string => `${modelId}_attribution_output_v2`
export const budgetOptimiserTableID = (modelId: number): string => `ia_curves_${modelId}_optimals_v1`

const BIGQUERY_SPEND_PLATFORM = {
  label: 'Google BigQuery',
  platform: PLATFORM.GOOGLE_BIGQUERY,
  iconUrl: `${ASSETS_BASE_URL}/google_bigquery.svg`,
  commonIconUrl: `${ASSETS_BASE_URL}/google.svg`,
  oauthUrl: 'fake_url_for_now',
  isSupportedByChannelGrouping: true,
  externalEntityLabel: 'views',
  externalEntitiesLabel: 'views',
  supportsServiceAccountAccess: false,
  isManualAccountInput: false
}

export const SUPPORTED_CUSTOM_SPEND_PLATFORMS = [
  BIGQUERY_SPEND_PLATFORM,
  PLATFORMS.find((p) => p.platform === PLATFORM.GOOGLE_SHEETS)
]

export const DEFINED_MODELS = {
  RBA: 'rba',
  GA4_DEFAULT: 'ga4Default',
  GA4_LNDA: 'ga4Lnda'
} as const

export const MODEL_TYPE_TITLE_KEYS = {
  xgboost: 'dashboard.modelTypeTitles.xgboost',
  ddav2: 'dashboard.modelTypeTitles.ddav2',
  lnda: 'dashboard.modelTypeTitles.lnda',
  native: 'dashboard.modelTypeTitles.native',
  rba: 'dashboard.modelTypeTitles.rba',
  ga4Default: 'dashboard.modelTypeTitles.ga4Default',
  ga4Lnda: 'dashboard.modelTypeTitles.ga4Lnda'
} as const

export type AttributionModelTitleId = keyof typeof MODEL_TYPE_TITLE_KEYS

export const isAttributionModelId = (modelId: string): modelId is AttributionModelTitleId =>
  Object.hasOwn(MODEL_TYPE_TITLE_KEYS, modelId)

/** Used by ChartLegend for model label/value lookup */
export const DEFINED_MODELS_FOR_LEGEND = [
  { value: DEFINED_MODELS.RBA, label: 'RBA' },
  { value: DEFINED_MODELS.GA4_DEFAULT, label: 'GA4 Default' },
  { value: DEFINED_MODELS.GA4_LNDA, label: 'GA4 Session-scoped' }
] as const

export const CUSTOM_SOURCE_TYPES = [
  { label: 'Online spend', value: CustomDataSourceType.ONLINE_SPEND },
  { label: 'Offline spend', value: CustomDataSourceType.OFFLINE_SPEND }
]

export const CUSTOM_SOURCE_VALIDATION_DAY_OPTIONS = [
  {
    label: 'Yesterday',
    value: 1
  },
  {
    label: 'Two days ago',
    value: 2
  },
  {
    label: 'Three days ago',
    value: 3
  },
  {
    label: 'No validation',
    value: 0
  }
]

export const GA4_EXCLUSION_LIST: number[] = [5158340522934272]

export const DEFAULT_BQ_DATASET = 'alvie_attribution'

export const RBA_MODEL_TYPE = 'rba'

export const CREDIT_INPUTS = {
  RBA: 'rba',
  GA4_DEFAULT: 'ga4Default',
  GA4_LNDA: 'ga4Lnda'
} as const

/**
 * `creditInput` values supported for attribution channel analytics (revenue/conversions column map; same as
 * `CREDIT_INPUTS`). The attribution dashboard UI only allows RBA — use `IS_ATTRIBUTION_DASHBOARD_CREDIT_INPUT` for that gate.
 */
export type AttributionSupportedModel = (typeof CREDIT_INPUTS)[keyof typeof CREDIT_INPUTS]

export const IS_ATTRIBUTION_SUPPORTED_MODEL = (
  modelType: string | undefined | null
): modelType is AttributionSupportedModel =>
  modelType != null &&
  modelType !== '' &&
  (Object.values(CREDIT_INPUTS) as AttributionSupportedModel[]).includes(modelType as AttributionSupportedModel)

/** Allowed `creditInput` on the attribution dashboard route (RBA only; see `Dashboard` shell). */
export const ATTRIBUTION_DASHBOARD_CREDIT_INPUT = CREDIT_INPUTS.RBA

export type AttributionDashboardCreditInput = typeof ATTRIBUTION_DASHBOARD_CREDIT_INPUT

export const IS_ATTRIBUTION_DASHBOARD_CREDIT_INPUT = (
  modelType: string | undefined | null
): modelType is AttributionDashboardCreditInput => modelType === ATTRIBUTION_DASHBOARD_CREDIT_INPUT

interface ModelType {
  value: AttributionModelTitleId
  color: ThemeColor
  pattern: LinePattern
  alert?: AlertType
}

export const getAttributionModelTypes = (theme: Theme): ModelType[] => [
  {
    value: 'rba',
    color: theme.tokens.dataViz.primary[1].fill.default,
    pattern: 'continuous'
  }
]

export const getGa4ModelTypes = (theme: Theme): ModelType[] => [
  {
    value: 'ga4Default',
    color: theme.tokens.dataViz.secondary[4].border.subtle,
    pattern: 'dash'
  },
  {
    value: 'ga4Lnda',
    color: theme.tokens.dataViz.primary[6].fill.default,
    pattern: 'dot'
  }
]

export const getModelTypes = (theme: Theme): ModelType[] => [
  ...getAttributionModelTypes(theme),
  ...getGa4ModelTypes(theme)
]

export const SELECT_METRICS = ['revenue', 'conversions'] as const

export const OPERATOR_OPTIONS = {
  [DIMENSION_OPERATORS_WITH_TYPES.is.id]: DIMENSION_OPERATORS_WITH_TYPES.is,
  [DIMENSION_OPERATORS_WITH_TYPES.not_is.id]: DIMENSION_OPERATORS_WITH_TYPES.not_is,
  [DIMENSION_OPERATORS_WITH_TYPES.one_of.id]: DIMENSION_OPERATORS_WITH_TYPES.one_of,
  [DIMENSION_OPERATORS_WITH_TYPES.not_one_of.id]: DIMENSION_OPERATORS_WITH_TYPES.not_one_of,
  [DIMENSION_OPERATORS_WITH_TYPES.contains.id]: DIMENSION_OPERATORS_WITH_TYPES.contains
}

export const getChannelUnmappedColor = (theme: Theme): ThemeColor => theme.tokens.foreground.main.disabled

export const attributionModelMetrics: AttributionMetric[] = [
  {
    id: 'sumSpend',
    label: 'Spend',
    type: 'currency',
    aggregation: 'sum',
    description: 'Total spend for the Channel'
  },
  {
    id: 'sumGa4Sessions',
    label: 'GA4 Sessions',
    type: 'number',
    aggregation: 'sum',
    description: 'Total GA4 sessions for the Channel',
    missingValue: 0
  },
  {
    id: 'sumGa4DefaultAttributionRevenue',
    label: 'GA4 Default Attribution Revenue',
    type: 'currency',
    aggregation: 'sum',
    description: 'Total revenue for the Channel with GA4 Default Attribution',
    missingValue: 0
  },
  {
    id: 'sumGa4DefaultAttributionConversions',
    label: 'Conversions',
    type: 'number',
    aggregation: 'sum',
    description: 'Total conversions for the Channel with GA4 Default Attribution',
    missingValue: 0
  },
  {
    id: 'sumGa4LndaAttributionRevenue',
    label: 'GA4 Session scoped Attribution Revenue',
    type: 'currency',
    aggregation: 'sum',
    description: 'Total revenue for the Channel with GA4 Session scoped Attribution',
    missingValue: 0
  },
  {
    id: 'sumGa4LndaAttributionConversions',
    label: 'GA4 Session scoped Attribution Conversions',
    type: 'number',
    aggregation: 'sum',
    description: 'Total conversions for the Channel with GA4 Session scoped Attribution',
    missingValue: 0
  },
  {
    id: 'sumRegressionBasedAttributionRevenue',
    label: 'Alvie Attribution Revenue',
    type: 'currency',
    aggregation: 'sum',
    description: 'Total revenue for the Channel with Alvie Attribution',
    missingValue: 0
  },
  {
    id: 'sumRegressionBasedAttributionConversions',
    label: 'Alvie Attribution Conversions',
    type: 'number',
    aggregation: 'sum',
    description: 'Total conversions for the Channel with Alvie Attribution',
    missingValue: 0
  },
  {
    id: 'avgDataSufficiency',
    label: 'Data Sufficiency',
    type: 'percentage',
    aggregation: 'any',
    description: 'Total data sufficiency',
    missingValue: 0
  },
  {
    id: 'avgVariationStrength',
    label: 'Variation Strength',
    type: 'percentage',
    aggregation: 'any',
    description: 'Total variation strength',
    missingValue: 0
  },
  {
    id: 'avgQualityOfFitRevenue',
    label: 'Revenue Quality of Fit',
    type: 'percentage',
    aggregation: 'average',
    description: 'Quality of fit level for the Channel Revenue',
    missingValue: 0
  },
  {
    id: 'avgQualityOfFitConversions',
    label: 'Conversions Quality of Fit',
    type: 'percentage',
    aggregation: 'average',
    description: 'Quality of fit level for the Channel Conversions',
    missingValue: 0
  },
  {
    id: 'avgStabilityScoreRevenue',
    label: 'Revenue Stability Score',
    type: 'percentage',
    aggregation: 'any',
    description: 'Total Stability score level for the Revenue',
    missingValue: 0
  },
  {
    id: 'avgStabilityScoreConversions',
    label: 'Conversions Stability Score',
    type: 'percentage',
    aggregation: 'any',
    description: 'Total Stability score level for the Conversions',
    missingValue: 0
  },
  {
    id: 'avgCompositeConfidenceRevenue',
    label: 'Revenue Confidence',
    type: 'percentage',
    aggregation: 'average',
    description: 'Composite confidence level for the Channel Revenue',
    missingValue: 0
  },
  {
    id: 'avgCompositeConfidenceConversions',
    label: 'Conversions Confidence',
    type: 'percentage',
    aggregation: 'average',
    description: 'Composite confidence level for the Channel Conversions',
    missingValue: 0
  },
  {
    id: 'sumImpressions',
    label: 'Impressions',
    type: 'number',
    aggregation: 'sum',
    description: 'Total impressions for the Channel',
    missingValue: 0
  }
]

export const getAttributionModelDimensions = (theme: Theme): AttributionDimension[] => [
  {
    id: 'date',
    label: 'Date',
    type: 'date',
    description: 'Date of the data',
    format: 'MMM do'
  },
  {
    id: 'channelId',
    label: 'Channel',
    type: 'string'
  },
  {
    id: 'model',
    label: 'Model',
    type: 'variant',
    newMetrics: {
      revenue: {
        label: 'Revenue',
        color: theme.tokens.foreground.semantic.info.default
      },
      conversions: {
        label: 'Conversions',
        color: theme.tokens.foreground.semantic.success.default
      }
    },
    variantsMap: {
      [CREDIT_INPUTS.RBA]: {
        revenue: 'sumRegressionBasedAttributionRevenue',
        conversions: 'sumRegressionBasedAttributionConversions'
      },
      [CREDIT_INPUTS.GA4_DEFAULT]: {
        revenue: 'sumGa4DefaultAttributionRevenue',
        conversions: 'sumGa4DefaultAttributionConversions'
      },
      [CREDIT_INPUTS.GA4_LNDA]: {
        revenue: 'sumGa4LndaAttributionRevenue',
        conversions: 'sumGa4LndaAttributionConversions'
      }
    }
  }
]

export const channelComparisonAxis: AttributionAxisProps = {
  type: 'bar',
  firstAxis: {
    id: 'channel',
    type: 'values',
    dimension: 'channelId',
    sort: chartDataRowSort,
    filter: 'valuedMetrics',
    change: {
      change: 'static',
      options: 'all'
    }
  },
  secondAxis: {
    id: 'metric',
    type: 'metricVariants',
    change: {
      change: 'internal',
      options: ['conversions', 'revenue']
    },
    variant: {
      id: 'metricModels',
      dimension: 'model',
      type: 'variantValues',
      description: 'Select models to compare',
      change: {
        change: 'internal',
        options: 'all'
      }
    },
    label: 'Model metric',
    description: 'Select metric for comparison'
  }
}

export const modelResultsAxis: AttributionAxisProps = {
  type: 'area',
  firstAxis: 'date',
  secondAxis: {
    id: 'metric',
    type: 'metricTransposed',
    change: {
      change: 'internal',
      options: ['conversions', 'revenue']
    },
    variant: {
      id: 'metricModel',
      dimension: 'model',
      type: 'variantValue',
      description: 'Select model for comparison',
      change: {
        change: 'internal',
        options: 'all'
      }
    },
    transpose: {
      id: 'transposedChannelId',
      type: 'values',
      dimension: 'channelId',
      sort: 'metricsDesc',
      change: {
        change: 'internal',
        options: 'all'
      }
    },
    label: 'Model metric',
    description: 'Select metric for comparison'
  }
}

export const getChannelModelResultsAxis = (theme: Theme): AttributionAxisProps => ({
  type: 'bar',
  otherType: 'line',
  mainOther: true,
  firstAxis: 'date',
  secondAxis: {
    id: 'metric',
    type: 'metric',
    change: {
      change: 'controlled',
      options: ['sumGa4Sessions', 'sumSpend'],
      display: true
    },
    label: 'Channel metric',
    color: theme.tokens.dataViz.secondary[1].fill.subtle,
    description: 'Select the first metric'
  },
  otherSecondAxis: {
    id: 'otherMetric',
    type: 'metricVariants',
    change: {
      change: 'controlled',
      options: ['conversions', 'revenue'],
      display: false
    },
    variant: {
      id: 'metricModels',
      dimension: 'model',
      type: 'variantValues',
      description: 'Select models for comparison',
      change: {
        change: 'internal',
        options: 'all'
      }
    },
    transpose: {
      id: 'transposedChannelId',
      type: 'value',
      dimension: 'channelId',
      change: {
        change: 'controlled',
        options: 'all',
        display: false
      }
    },
    filter: true,
    label: 'Metric',
    description: 'Select the second metric'
  }
})

export const CHANNEL_RESULTS_POPUP_QUERY_PARAMETER_KEY = 'channelResults'
export const CHANNEL_DETAILS_QUERY_PARAMETER_KEY = 'channelId'
export const CHANNEL_RESULTS_DATE_RANGE_QUERY_PARAMETER_KEY = 'channelResultsDateRange'
export const CHANNEL_RESULTS_METRIC_QUERY_PARAMETER_KEY = 'channelResultsMetric'
